import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Shield, Heart, X, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const DISMISSED_KEY = 'papo-donation-dismissed';

const DONATION_ADDRESSES = [
  { chain: 'Ethereum (ETH)', address: '0xe052Dcb90c9e1329D6072d4588C2d92194875b23', color: '#627EEA' },
  { chain: 'Bitcoin SegWit', address: 'bc1qgemtywjhr8syr3pahx5kmhvjcg0kkz4j9ml7ny', color: '#F7931A' },
  { chain: 'Solana (SOL)', address: 'GmiYxhEc8NnpuozXuW6Ec1ACKUFomKjAU6ESnf4QVSG8', color: '#9945FF' },
  { chain: 'Tron (TRX)', address: 'TEmbcNtKydHE8iHnpEwvwT5Qx6aLrN5vub', color: '#EB0029' },
  { chain: 'BNB Chain', address: '0xe052Dcb90c9e1329D6072d4588C2d92194875b23', color: '#F0B90B' },
];

const DonationModal = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const dismissed = sessionStorage.getItem(DISMISSED_KEY);
    if (!dismissed) {
      const timer = setInterval(() => {
        if (!document.querySelector('[role="dialog"]') && !window.location.pathname.startsWith('/live')) { setOpen(true); clearInterval(timer); }
      }, 2000);
      return () => clearInterval(timer);
    }
  }, [isAuthenticated]);

  const handleClose = () => { setOpen(false); sessionStorage.setItem(DISMISSED_KEY, 'true'); };

  const copyAddress = (address, idx) => {
    navigator.clipboard.writeText(address);
    setCopiedIdx(idx);
    toast.success(t('donation.addressCopied'));
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-lg rounded-3xl p-0 overflow-hidden gap-0" data-testid="donation-modal" aria-describedby="donation-description">
        <div className="gradient-warm p-6 pb-8 relative">
          <button onClick={handleClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition" data-testid="donation-close-x"><X className="w-4 h-4" /></button>
          <DialogTitle className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center"><Shield className="w-6 h-6 text-white" /></div>
            <div>
              <span className="text-xl font-bold text-white font-['Nunito'] block">{t('donation.title')}</span>
              <span className="text-white/80 text-sm font-normal block">{t('donation.subtitle')}</span>
            </div>
          </DialogTitle>
        </div>
        <div className="p-6 -mt-3 bg-[var(--card-bg)] rounded-t-3xl relative max-h-[60vh] overflow-y-auto">
          <div id="donation-description" className="space-y-4 text-[var(--text-primary)]">
            <p className="leading-relaxed">{t('donation.body')}</p>
            <div className="space-y-3">
              <p className="font-semibold text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-[var(--papo-red)]" />{t('donation.addresses')}</p>
              {DONATION_ADDRESSES.map((item, idx) => (
                <div key={item.chain} className="bg-[var(--elevated-bg)] rounded-2xl p-3" data-testid={`donation-address-${idx}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color: item.color }}>{item.chain}</span>
                    <button onClick={() => copyAddress(item.address, idx)} className="flex items-center gap-1 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition" data-testid={`copy-address-${idx}`}>
                      {copiedIdx === idx ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      {copiedIdx === idx ? t('donation.copied') : t('donation.copy')}
                    </button>
                  </div>
                  <p className="text-xs font-mono text-[var(--text-secondary)] break-all select-all">{item.address}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-[var(--text-secondary)]">{t('donation.footer')}</p>
          </div>
          <div className="flex gap-3 mt-6">
            <Button onClick={handleClose} className="flex-1 gradient-warm text-white rounded-xl h-11 font-bold" data-testid="donation-understood-btn">{t('donation.close')}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DonationModal;
